/**
 * SDK facts and the copy derived from them.
 *
 * Every SDK claim rendered on the site reads from here, so page copy cannot
 * drift from the package. `version` is duplicated from the docs repo's
 * `src/sidebar.mjs` (`site.version`) because the two repos do not share a
 * build; bump both on release.
 *
 * Verified against the SDK source metadata for briefcase-ai: version 4.4.0,
 * requires_python >=3.9, Apache-2.0.
 */

export const SDK = {
  version: '4.4.0',
  install: 'pip install briefcase-ai',
  license: 'Apache-2.0',
  python: 'Python 3.9+',
  docsUrl: 'https://docs.briefcaseai.org',
  repoUrl: 'https://github.com/briefcasebrain/briefcase-ai-sdk',
} as const;

/** Absolute URL for a docs path, e.g. docs('/features/replay/'). */
export const docs = (path: string): string => `${SDK.docsUrl}${path}`;

export interface AuditShape {
  /** Stakes label, used as the rung's eyebrow. */
  stakes: string;
  title: string;
  /** The failure the reader recognizes. */
  symptom: string;
  /** The mechanism that answers it. */
  answer: string;
  linkLabel: string;
  docsPath: string;
}

/**
 * The same question at three stakes levels. Rungs 1 and 2 stay in engineering
 * language; the audit and policy vocabulary belongs to rung 3.
 */
export const AUDIT_SHAPES: AuditShape[] = [
  {
    stakes: 'Engineering',
    title: "When an agent does something you can't undo",
    symptom:
      'An agent ran a destructive tool call. You have the traceback, not the reason it was allowed to run.',
    answer:
      'A guardrail evaluates the action before it executes. Deny-by-default, so the action proceeds only on an explicit allow.',
    linkLabel: 'Guardrails',
    docsPath: '/features/guardrails/',
  },
  {
    stakes: 'Review',
    title: 'When someone says "show me your work"',
    symptom:
      'You have the output. Not the inputs, the model parameters, the confidence, or the evidence it leaned on.',
    answer:
      'Every decision is recorded with all of it, and replays deterministically against a fresh run.',
    linkLabel: 'Decision recording',
    docsPath: '/features/decision-recording/',
  },
  {
    stakes: 'Trust',
    title: 'When the answer has to hold up months later',
    symptom:
      'An auditor asks who approved a $400K wire on March 14. The log shows the action. Nobody can reconstruct the rules, inputs, or counter-party data that justified it.',
    answer:
      "Route through versioned policy, reconstruct what was known at the decision's moment, and seal it into a hash-verified bundle.",
    linkLabel: 'Audit bundles',
    docsPath: '/features/compliance-bundles/',
  },
];

export interface LifecycleAct {
  act: string;
  summary: string;
  /** Primitive names as they appear in the SDK. */
  primitives: string[];
  code: string;
  docsPath: string;
}

/**
 * The five acts, in the order the SDK docs use them. Snippets are the shortest
 * form that still names the real primitive.
 */
export const LIFECYCLE: LifecycleAct[] = [
  {
    act: 'Capture',
    summary:
      'Wrap a decision point. Inputs, outputs, model parameters, and timing are recorded without changing what the function returns.',
    primitives: ['@briefcase.capture', 'observe()'],
    code: `import briefcase

mem = briefcase.observe("memory")

@briefcase.capture(decision_type="triage")
def classify_ticket(text: str) -> str:
    return model.classify(text)`,
    docsPath: '/features/decision-recording/',
  },
  {
    act: 'Control',
    summary:
      'Evaluate whether the action is allowed before it runs. Composable, side-effect-free, and closed by default.',
    primitives: ['GuardrailEnv', 'DenyByDefaultWrapper', 'Effect.DENY'],
    code: `env = DenyByDefaultWrapper(
    TierGuardrail(allowed_tiers={"premium"})
)

result = env.evaluate(EvalRequest(
    agent="support-bot", action="refund",
    resource="order/91f2", context=ctx,
))
if not result.is_allowed:
    raise PermissionError(result.reason)`,
    docsPath: '/features/guardrails/',
  },
  {
    act: 'Store & Query',
    summary:
      'Keep an append-only trail. Corrections are appended, never overwritten, so you can still read what was known at the time.',
    primitives: ['SqliteBackend', 'AsOfView'],
    code: `backend = SqliteBackend("./decisions.db")
decision_id = backend.save_decision(decision)

# what was known when it decided, not now
with AsOfView(store, transaction_time=ts) as view:
    view.latest("policy:max_wire_usd").value`,
    docsPath: '/features/bitemporal-storage/',
  },
  {
    act: 'Replay & Verify',
    summary:
      'Re-run a stored decision and compare it against the original. Measure how far a set of outputs has drifted.',
    primitives: ['ReplayEngine', 'DriftCalculator'],
    code: `engine = ReplayEngine(backend)
result = engine.replay(decision_id, "strict")
print(result.status, result.outputs_match)

drift = DriftCalculator().calculate_drift(outputs)
print(drift.agreement_rate)`,
    docsPath: '/features/replay/',
  },
  {
    act: 'Prove',
    summary:
      'Reconstruct the decision under the policy version that was actually in effect, then seal it so tampering is detectable.',
    primitives: ['AgentRouter', 'ExaminerBundle'],
    code: `decision = router.route(
    ctx, as_of_transaction_time=march_14,
)
print(decision.policy_version)

bundle = ExaminerBundle.build(
    decision, evidence, registry,
)
bundle.verify()  # raises if a byte changed`,
    docsPath: '/features/compliance-bundles/',
  },
];
